import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { Box, Button, CircularProgress, Icon, Stack, Typography } from '@mui/material'
import { jwtDecode } from 'jwt-decode'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '../../Shared/Contexts/AuthContext'
import { useTests } from '../Contexts/TestsContext'

const TestSite = ({ tracker, gateway, setResult = () => { } }) => {
  const { authState, currentUser } = useAuth()
  const { usePollDevicesMeasures, useSaveDeviceTest, useGetSiteInfo, useGetTestConfig } = useTests()
  const navigate = useNavigate()

  const { email } = jwtDecode(authState.accessToken)

  const [stopPolling, setStopPolling] = useState(false)
  const [timeoutId, setTimeoutId] = useState(null)
  const [intervalId, setIntervalId] = useState(null)
  const [testResult, setTestResult] = useState(null)
  const [measuresNumber, setMeasuresNumber] = useState(0)
  const [testFailure, setTestFailure] = useState(false)
  const [success, setSuccess] = useState(false)
  const [siteValidation, setSiteValidation] = useState(null)
  const [retry, setRetry] = useState(false)
  const [startTest] = useState(new Date().toISOString())
  const [testLimits, setTestLimits] = useState({ snr: 0, rssi: 0 })
  const [successColor, setSuccessColor] = useState('success')

  const pollFunction = () => {
    usePollDevicesMeasures(startTest, tracker.id).then((res) => {
      let tmp_res = null
      res.data.reverse().forEach((e) => {
        if (e.payload != null && e.linkStatus != null) {
          tmp_res = e
        }
      })

      setTestResult(tmp_res)

      setMeasuresNumber(res.data.length)
      if (res.data.length >= 7) {
        if (tmp_res.linkStatus?.gateways[0].rssi > Number(testLimits.rssi) || tmp_res.linkStatus?.gateways[0].snr > Number(testLimits.snr)) {
          setSuccessColor('success')
        }
        else {
          setSuccessColor('warning')
        }
        setStopPolling(true)
      }
    })
  }

  const handleSaveTestResult = () => {
    const payload = {
      timestamp: new Date().toISOString(),
      sf: siteValidation.sf,
      snr: siteValidation.snr,
      rssi: siteValidation.rssi,
      messagesCount: siteValidation.messagesCount,
      networkStatus: siteValidation.networkStatus,
      averagePosition: {
        latitude: siteValidation.averagePosition.latitude,
        longitude: siteValidation.averagePosition.longitude
      },
      averageGatewayCount: siteValidation.averageGatewayCount,
      fieldTestDevice: {
        deviceId: tracker.id,
        name: tracker.name,
        organizationKey: currentUser.currentOrganizationKey
      },
      position: {
        latitude: testResult?.payload?.latitude,
        longitude: testResult?.payload?.longitude
      },
      operator: {
        user: authState.name,
        email: email
      }
    }

    useSaveDeviceTest(payload, tracker.id).then(() => {
      navigate('/select-test')
    })
  }

  const handleRetryTest = () => {
    setRetry(!retry)
    setSuccess(false)
    setResult(null)
    setSiteValidation(null)
    setMeasuresNumber(0)
    setTestResult(null)
    setTestFailure(false)
  }

  useEffect(() => {
    const POLLING_INTERVAL = 10000   // 10 secondi
    const TOTAL_DURATION = 300000   // 5 minuti

    if (tracker == null || gateway == null) {
      return
    }

    const intervalId = setInterval(pollFunction, POLLING_INTERVAL)

    const timeoutId = setTimeout(() => {
      clearInterval(intervalId)
      setTestFailure(true)
    }, TOTAL_DURATION)

    setIntervalId(intervalId)
    setTimeoutId(timeoutId)
  }, [tracker, gateway, retry])

  useEffect(() => {
    if (stopPolling && intervalId && timeoutId && (testResult || testFailure)) {
      setStopPolling(false)
      clearInterval(intervalId)
      clearTimeout(timeoutId)

      useGetSiteInfo(tracker.id, startTest).then((res) => {
        setSiteValidation(res.data)
        setSuccess(true)
        setResult(true)
      })
    }
  }, [stopPolling, testResult])

  useEffect(() => {
    useGetTestConfig().then((res) => { setTestLimits(res.data) })
  }, [])

  return testFailure ? (
    <Stack
      display="flex"
      flexDirection="column"
      marginTop={5}
    >
      <Typography variant="h4" textAlign={'center'} marginBottom={5}>
        Test fallito
      </Typography>

      <Button onClick={handleRetryTest} variant='outlined' sx={{ marginTop: '10px', marginBottom: '5px' }}>Riprova</Button>
    </Stack>
  ) : (
    <Stack
      display="flex"
      flexDirection="column"
      marginTop={5}
    >
      <Typography variant="h4" textAlign={'center'} marginBottom={5}>
        {success ? 'Test completato con successo!' : 'In corso...'}
      </Typography>

      <Typography variant="body1" textAlign={'center'} marginBottom={2}>
        Controlla il Tester e attendi l&apos;invio dei pacchetti
      </Typography>

      <Box display='flex' justifyContent='center' marginBottom={5}>
        {success ? <Icon color={successColor} sx={{ fontSize: '8rem' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: '8rem' }} />
        </Icon> : <CircularProgress size={'5rem'} />}
      </Box>

      <Typography variant="body1" textAlign={'left'} fontWeight={'bold'} marginBottom={'5px'} id="recieved-mesures">
        {measuresNumber} misure ricevute
      </Typography>

      <Typography variant="body2" textAlign={'left'} fontWeight={'bold'}>
        Ultimo uplink
      </Typography>
      <Box
        display="flex"
        justifyContent="space-around"
      >
        <Typography variant="body2" textAlign={'left'}>
          sf
        </Typography>
        <Typography variant="body2" textAlign={'left'}>
          freq
        </Typography>
      </Box>
      <Box
        display="flex"
        justifyContent="space-around"
        backgroundColor={'#e0e0e0'}
        borderRadius={'10px'}
      >
        <Typography variant="body2" textAlign={'left'}>
          {testResult?.statistics.sf ? testResult?.statistics.sf : '-'}
        </Typography>
        <Typography variant="body2" textAlign={'left'}>
          {testResult?.statistics.freq ? testResult?.statistics.freq : '-'}
        </Typography>
      </Box>

      <Typography variant="body2" textAlign={'left'} fontWeight={'bold'}>
        Top gateway
      </Typography>
      <Box
        display="flex"
        justifyContent="space-around"
      >
        <Typography variant="body2" textAlign={'left'}>
          board
        </Typography>
        <Typography variant="body2" textAlign={'left'}>
          snr
        </Typography>
        <Typography variant="body2" textAlign={'left'}>
          mac
        </Typography>
        <Typography variant="body2" textAlign={'left'}>
          rssi
        </Typography>
      </Box>
      <Box
        display="flex"
        justifyContent="space-around"
        backgroundColor={'#e0e0e0'}
        borderRadius={'10px'}
      >
        <Typography variant="body2" textAlign={'left'}>
          {testResult?.linkStatus?.gateways[0].board != null ? testResult?.linkStatus.gateways[0].board : '-'}
        </Typography>
        <Typography variant="body2" textAlign={'left'}>
          {testResult?.linkStatus?.gateways[0].snr ? testResult?.linkStatus.gateways[0].snr : '-'}
        </Typography>
        <Typography variant="body2" textAlign={'left'}>
          {testResult?.linkStatus?.gateways[0].mac ? testResult?.linkStatus.gateways[0].mac : '-'}
        </Typography>
        <Typography variant="body2" textAlign={'left'}>
          {testResult?.linkStatus?.gateways[0].rssi ? testResult?.linkStatus.gateways[0].rssi : '-'}
        </Typography>
      </Box>

      {
        success ?
          <>
            <Button onClick={handleSaveTestResult} variant='contained' sx={{ marginTop: '100px' }}>Salva</Button>
            <Button onClick={handleRetryTest} variant='outlined' sx={{ marginTop: '10px', marginBottom: '5px' }}>Riprova</Button>
          </> : null
      }
    </Stack>
  )
}

export default TestSite